import { byteText, fill, inspectPath, t, type InspectAnswer } from '@bms2/shared';
import { Modal } from '../../components/modals/Modal';
import { LoadFailure } from '../../components/composites/LoadFailure';
import { useApi } from './api';
import styles from './RecordPage.module.css';
import own from './FileStorageBlock.module.css';

/**
 * 🔒 Decision 7620 (T3124, I3a): Inspect, opened from a record's banner menu by
 * a super admin. One read, one window: the record's id and version counter,
 * every table and column that names it with how many rows do, its audit row
 * count, and the stored objects its files name. 🔨 Read-only and counts alone:
 * the window offers nothing to press but Close, and mounts only while open, so
 * each opening reads the counts afresh.
 */
export function InspectWindow({ recordType, recordId, title, apiBaseUrl = '', onClose }: { recordType: string; recordId: string; title: string; apiBaseUrl?: string; onClose: () => void }) {
  const read = useApi<InspectAnswer>(inspectPath(recordType, recordId), apiBaseUrl);
  const answer = read.state.kind === 'ready' ? read.state.data : null;
  return (
    <Modal open title={t('record.inspect.title')} subtitle={title} onCancel={onClose} dataModal="record-inspect" actions={[{ kind: 'close', label: t('modal.close'), onAct: onClose }]}>
      {read.state.kind === 'failed' ? <LoadFailure state={read.state} onRetry={read.reload} /> : null}
      {answer ? (
        <div className={own.lines} data-inspect-record={answer.recordId} data-inspect-version={answer.version ?? ''} data-inspect-audit={answer.auditRows}>
          <p className={styles.hint}>{t('record.inspect.hint')}</p>
          <span>{fill(t('record.inspect.id'), { id: answer.recordId, table: answer.table })}</span>
          <span>{answer.version === null ? t('record.inspect.noVersion') : fill(t('record.inspect.version'), { version: answer.version })}</span>
          <span className={styles.fieldLabel}>{t('record.inspect.pointers')}</span>
          <span className={styles.hint}>{fill(answer.source === 'mergeMap' ? t('record.inspect.fromMergeMap') : t('record.inspect.fromReferences'), { count: answer.columnsRead })}</span>
          {answer.pointers.length === 0 ? <span className={styles.hint}>{t('record.inspect.noPointers')}</span> : null}
          <ul className={styles.versionList}>
            {answer.pointers.map((row) => (
              <li key={`${row.table}.${row.column}`} data-inspect-pointer={`${row.table}.${row.column}`} data-inspect-count={row.count}>
                {fill(t('record.inspect.pointer'), { table: row.table, column: row.column, count: row.count })}
              </li>
            ))}
          </ul>
          <span data-inspect-audit-line="true">{fill(t('record.inspect.audit'), { count: answer.auditRows })}</span>
          {answer.files === null
            ? <span className={styles.hint}>{t('record.inspect.noFiles')}</span>
            : <span data-inspect-files={answer.files.files}>{fill(t('record.inspect.files'), { files: answer.files.files, versions: answer.files.versions, objects: answer.files.objects, size: byteText(answer.files.bytes), sealed: answer.files.sealed })}</span>}
          {answer.objects.length > 0 ? (
            <ul className={styles.versionList}>
              {answer.objects.map((object) => (
                <li key={object.key} data-inspect-object={object.key}>
                  {fill(t('record.inspect.object'), { key: object.key, size: byteText(object.sizeBytes), seal: object.sealed ? t('record.inspect.objectSealed') : t('record.inspect.objectPlain'), versions: object.versions })}
                </li>
              ))}
            </ul>
          ) : null}
          {answer.moreObjects > 0 ? <span className={styles.hint}>{fill(t('record.inspect.moreObjects'), { count: answer.moreObjects })}</span> : null}
        </div>
      ) : null}
    </Modal>
  );
}
